// client/src/components/layout/TabHost.tsx (TypeScript since ST-00216)
//
// Renders one mounted pane per tab. Inactive panes stay mounted but hidden, so a
// tab keeps its filters, scroll position and loaded tables while you work in
// another one. Each pane is its own scroll container, which is what makes scroll
// position per-tab rather than shared. Within a tab, a new page opens at the top
// and Back / Forward returns to where it was left (PageScrollRestoration, ST-00087).
import {
  useCallback,
  useEffect,
  useRef,
  type Context,
  type HTMLAttributes,
  type ReactNode,
  type RefObject,
} from 'react';
import {
  MemoryRouter,
  useLocation,
  useNavigate,
  type Location,
  type NavigateFunction,
} from 'react-router-dom';
import { useTabs as useTabsJs } from '../../context/TabContext';
import TabVisibilityContext from '../../context/TabVisibilityContext';
import TabPaneContextJs from '../../context/TabPaneContext';
import useNavigationHistoryJs from '../../hooks/useNavigationHistory';
import AppRoutes, { type AppRoutesProps } from './AppRoutes';
import PageScrollRestoration from './PageScrollRestoration';
import { RouteErrorBoundary } from '../common/ErrorBoundary';
import type { TabNavState, TabsView } from '../../types/clientShell';
import './TabHost.css';

// Typed views of the still-JavaScript tab context, pane context and history hook.
const useTabs = (): TabsView => useTabsJs() as unknown as TabsView;
/** The id of the tab whose pane is rendering; null outside the tab host. */
const TabPaneContext = TabPaneContextJs as unknown as Context<string | null>;
const useNavigationHistory = useNavigationHistoryJs as unknown as (
  navigate?: NavigateFunction
) => TabNavState;

function urlOf(location: Location): string {
  return `${location.pathname}${location.search}${location.hash}`;
}

/**
 * Lives inside a tab's MemoryRouter. Reports that tab's location upward, keeps
 * the address bar pointed at the active tab, and publishes the tab's own
 * back/forward state for the shared NavigationBar.
 */
function TabRouterBridge({ tabId }: { tabId: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { activeId, registerNavigator, updateTabLocation, publishNav, syncAddressBar } = useTabs();

  const isActive = activeId === tabId;
  const url = urlOf(location);

  // useNavigate() returns a fresh function on every location change. Everything
  // downstream of it here feeds an effect that writes to context, so passing it
  // through directly would re-fire those effects on every navigation and spin.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const stableNavigate = useCallback<NavigateFunction>(
    (...args: unknown[]) => (navigateRef.current as (...a: unknown[]) => void)(...args),
    []
  );

  const { canGoBack, canGoForward, goBack, goForward, currentLabel } =
    useNavigationHistory(stableNavigate);

  useEffect(
    () => registerNavigator(tabId, stableNavigate),
    [tabId, stableNavigate, registerNavigator]
  );

  useEffect(() => {
    updateTabLocation(tabId, url);
  }, [tabId, url, updateTabLocation]);

  useEffect(() => {
    if (isActive) syncAddressBar(tabId, url);
  }, [isActive, tabId, url, syncAddressBar]);

  useEffect(() => {
    publishNav(tabId, { canGoBack, canGoForward, goBack, goForward, currentLabel });
  }, [publishNav, tabId, canGoBack, canGoForward, goBack, goForward, currentLabel]);

  return null;
}

interface TabPaneProps {
  active: boolean;
  tabId: string;
  /** The pane's content, given the pane element (the tab's scroll container). */
  children: (paneRef: RefObject<HTMLDivElement | null>) => ReactNode;
}

const hiddenPaneProps: HTMLAttributes<HTMLDivElement> = { inert: true, 'aria-hidden': true };

function TabPane({ active, tabId, children }: TabPaneProps) {
  const paneRef = useRef<HTMLDivElement>(null);
  return (
    <div
      ref={paneRef}
      className={`tab-pane${active ? '' : ' tab-pane--hidden'} page-pane`}
      // `inert` keeps focus and assistive tech out of hidden panes. React 19 knows it as a
      // boolean: `inert: ''` (the React 18 workaround) reads as false and rendered nothing,
      // so a hidden tab's controls stayed focusable (ST-00230).
      {...(active ? {} : hiddenPaneProps)}
    >
      <TabPaneContext.Provider value={tabId}>
        <TabVisibilityContext.Provider value={active}>
          {children(paneRef)}
        </TabVisibilityContext.Provider>
      </TabPaneContext.Provider>
    </div>
  );
}

export default function TabHost(routeProps: AppRoutesProps) {
  const { tabs, activeId } = useTabs();

  return (
    <div className="tab-viewport">
      {tabs
        .filter((tab) => !tab.discarded)
        .map((tab) => (
          <TabPane key={tab.id} tabId={tab.id} active={tab.id === activeId}>
            {(paneRef) => (
              <MemoryRouter initialEntries={[tab.url]} initialIndex={0}>
                <TabRouterBridge tabId={tab.id} />
                <PageScrollRestoration paneRef={paneRef} />
                {/* A page error stays in its own tab (ST-00215, PLT-3). */}
                <RouteErrorBoundary>
                  <AppRoutes {...routeProps} />
                </RouteErrorBoundary>
              </MemoryRouter>
            )}
          </TabPane>
        ))}
    </div>
  );
}
